import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Leaf } from "lucide-react";
import { PROFILE, IMAGES } from "../data/content";
import { CountUp, scrollToId, AvailabilityBadge } from "./shared";
import { SectionIndex, CoordReadout, AstrolabeGlyph, PlanetDot } from "./motifs";

const LINES = ["Systems that", "feel alive.", "Support that", "anticipates."];

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 600], [0, 60]);
  const glowY = useTransform(scrollY, [0, 600], [0, -40]);
  const [tagIdx, setTagIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTagIdx((i) => (i + 1) % PROFILE.taglines.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="top" className="relative overflow-hidden pt-28 sm:pt-32 pb-20 sm:pb-28">
      {/* golden light bloom */}
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-32 -right-24 w-[46rem] h-[46rem] rounded-full blur-3xl"
        aria-hidden
      >
        <div
          className="w-full h-full rounded-full"
          style={{ background: "radial-gradient(circle, rgba(245,215,126,0.55), rgba(212,176,92,0.15) 45%, transparent 70%)" }}
        />
      </motion.div>

      {/* faint hairline coordinate grid */}
      <div className="pointer-events-none absolute inset-0 hairline-grid opacity-40" aria-hidden />

      {/* top-left instrumentation strip */}
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-8" aria-hidden>
          <SectionIndex n={0} label="Observatory · Hero" />
          <span className="hidden sm:inline-flex items-center gap-2 ml-auto">
            <AstrolabeGlyph size={14} color="#B08830" />
            <CoordReadout>34°01′ N · 118°24′ W</CoordReadout>
          </span>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
        {/* LEFT — Typography */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <PlanetDot />
            <span className="font-mono-tech text-[11px] tracking-[0.28em] uppercase text-terracotta">
              {PROFILE.title}
            </span>
          </div>

          <div className="mb-6"><AvailabilityBadge /></div>

          <h1 className="headline-serif text-caramel text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02]">
            {LINES.map((line, i) => (
              <span key={`hero-line-${line}`} className="block overflow-hidden pb-[0.06em]">
                <motion.span
                  className="block pb-[0.04em]"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i % 2 === 1 ? <span className="italic text-terracotta">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-7 max-w-xl text-caramel/80 text-lg leading-relaxed"
          >
            {PROFILE.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-5 h-6 font-mono-tech text-sm text-olive flex items-center gap-2"
            data-testid="hero-rotating-tagline"
          >
            <AstrolabeGlyph size={13} color="#6B7C4A" />
            <motion.span key={tagIdx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              {PROFILE.taglines[tagIdx]}
            </motion.span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-cta-experience"
              data-cosmic="true"
              onClick={() => scrollToId("experience")}
              className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-ivory px-6 py-3 font-medium hover:bg-warmdark transition-colors warm-glow"
            >
              Tailor my resume
              <ArrowDownRight size={18} className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-contact"
              data-cosmic="true"
              onClick={() => scrollToId("contact")}
              className="inline-flex items-center gap-2 rounded-full border border-caramel/25 px-6 py-3 text-caramel hover:border-terracotta hover:text-terracotta transition-colors"
            >
              Get in touch
            </button>
          </motion.div>

          {/* stats — as "instrumentation readout" */}
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-md observatory-frame pt-5 pl-5 pb-5 pr-5">
            {PROFILE.stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 + i * 0.1, duration: 0.6 }}
                data-testid={`hero-stat-${i}`}
                className="relative min-w-0"
              >
                <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-brass mb-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display text-3xl sm:text-4xl text-terracotta leading-none">
                  <CountUp value={s.value} />
                </div>
                <div className="mt-2 font-mono-tech text-[10px] tracking-wide uppercase text-warmmuted leading-tight break-words">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* RIGHT — the still-life, motion contained to the image itself */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <motion.div
            style={{ y: imgY }}
            className="relative rounded-[1.6rem] overflow-hidden bg-linen shadow-[0_30px_80px_rgba(74,51,34,0.18)] border border-border observatory-frame"
          >
            <img
              src={IMAGES.hero}
              alt="Warm biophilic atrium at golden hour with brass armillary sphere and cascading pothos"
              className="w-full h-[440px] sm:h-[540px] object-cover kenburns"
              loading="eager"
              fetchPriority="high"
            />
            {/* corner instrumentation on the image */}
            <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-ivory/85 backdrop-blur px-3 py-1.5 border border-brass/30">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
              <span className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-caramel">Field · 01</span>
            </div>
            <div className="absolute bottom-3 right-3 rounded-full bg-warmdark/70 backdrop-blur px-3 py-1.5">
              <span className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-honey">Aperture · f/1.4</span>
            </div>
          </motion.div>

          <div className="absolute -bottom-5 -left-5 bg-ivory rounded-2xl border border-border shadow-[0_10px_40px_rgba(74,51,34,0.1)] px-5 py-4 flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-sage/40 text-forest">
              <Leaf size={18} />
            </span>
            <div>
              <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted">Based in</div>
              <div className="text-caramel font-semibold text-sm">{PROFILE.location}</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
